import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { isPresent, isBlank } from '@seedlang/utils';
import Text from 'components/text';
import Alert from 'components/alert';
import { AppUI, VocabUI } from '@seedlang/state';
import { NativeSelect } from '@mantine/core';
import Button from 'components/button/button';
import InPlaceSelect from 'components/form/in_place_select';
import { Constants, Theme } from '@seedlang/constants';
import InPlaceText from 'components/form/in_place_text';
import SeedlangExplainer from './seedlang_explainer';
import { SeedlangIconWithCircle } from '@seedlang/icons';

const Wrapper = styled.div`
`;

const ReviewOptionsWrapper = styled.div`
  width: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  padding: ${props => props.shaded ? '20px' : '10px 0 0'};
  box-sizing: border-box;
  background: ${props => props.shaded ? Theme.lightGray : 'transparent'};
  margin-bottom: 10px;
  position: relative;
  border-radius: ${props => props.shaded ? '12px' : '0'};
  input {
    height: 30px!important;
    width: calc(100% - 40px)!important;
    margin-left: 20px;
  }
  position: relative;
`;

const OptionsRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px 20px;
`;

const OptionField = styled.div`
  max-width: 100%;
  min-width: 180px;
`;

const toSelectData = options => (options || []).filter(Boolean).map(item => ({
  label: String(item[1]),
  value: String(item[0]),
}));

const AddButtonWrapper = styled.div`
  width: 100%;
  min-height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AbsoluteContainer = styled.div`
  position: absolute;
  inset: 0;
  width: fit-content;
  height: fit-content;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

export const SeedlangDisclaimer = styled.div`
  background: #1DB5D7;
  border-radius: 0 0 12px 0;
  padding: 10px;
`;

export const AddToReviews = ({
  buttonText = 'Add to Reviews',
  cardTypeId,
  cardTypeOptions,
  confirmationMessage = 'Your reviews were successfully created.',
  deckButtonDisabled,
  deckId,
  isPodcastEpisode,
  newReviewDeckName,
  onCreateNewReviewDeck,
  onCreateReviews,
  onSetCardTypeId,
  onSetDeckId,
  onSetNewReviewDeckName,
  reviewDeckOptions,
  selectedWordIds,
  selectedWordsExcludedFromConjugationTrainer,
  selectedWordsExcludedFromGenderTrainer,
  selectedWordsExcludedFromPluralTrainer,
  showReviewsCreatedMessage,
  siteIsSeedlang,
}) => {
  const useNativeSelect = buttonText === 'Add to Seedlang';
  const addButton = (
    <Button
      margin={siteIsSeedlang ? '4px 0 10px' : '0'}
      onClick={onCreateReviews}
      disabled={deckButtonDisabled}
      background={Theme.blue}
    >
      {buttonText === 'Add to Seedlang' ? (
        <SeedlangIconWithCircle width='20px' style={{marginRight: '8px'}} />
      ) : (
        <i className='fa fa-plus'/>
      )}
      {buttonText}
    </Button>
  );

  return (
    <Wrapper>
      {
        showReviewsCreatedMessage &&
          <Alert
            textAlign='center'
            width='100%'
            state='success'
          >
            <div dangerouslySetInnerHTML={{ __html: confirmationMessage }} />
          </Alert>
      }
      {
        isPresent(selectedWordIds) && isBlank(reviewDeckOptions) &&
        <ReviewOptionsWrapper shaded={!siteIsSeedlang}>
          {
              !siteIsSeedlang &&
                <SeedlangExplainer helpInCorner isPodcastEpisode={isPodcastEpisode} />
          }
          <Text
            fontSize='12px'
            bold
            margin='10px 0 0 0'
            color='#333'
          >
            Review Deck
          </Text>
          <InPlaceText
            allowUpdate
            show
            placeholder='Review Deck Name'
            defaultValue={newReviewDeckName}
            onChange={onSetNewReviewDeckName}
          />
          <Button
            margin='10px 0 10px 0'
            onClick={onCreateNewReviewDeck}
            disabled={isBlank(newReviewDeckName)}
          >
            <i className='fa fa-plus'/>
            Create Review Deck
          </Button>
        </ReviewOptionsWrapper>
      }
      {
        isPresent(selectedWordIds) && isPresent(reviewDeckOptions) &&
          <ReviewOptionsWrapper shaded={!siteIsSeedlang}>
            <OptionsRow>
              <OptionField>
                <Text center fontSize='12px' bold margin='0 0 5px' color='#333'>
                  Review Deck
                </Text>
                {useNativeSelect ? (
                  <NativeSelect
                    data={toSelectData(reviewDeckOptions)}
                    onChange={event => onSetDeckId(event.currentTarget.value)}
                    size='sm'
                    value={deckId ? String(deckId) : ''}
                  />
                ) : (
                  <InPlaceSelect
                    allowUpdate
                    height='45px'
                    options={reviewDeckOptions}
                    value={deckId}
                    onChange={onSetDeckId}
                  />
                )}
              </OptionField>
              <OptionField>
                <Text center fontSize='12px' bold margin='0 0 5px' color='#333'>
                  Card Type
                </Text>
                {useNativeSelect ? (
                  <NativeSelect
                    data={toSelectData(cardTypeOptions)}
                    onChange={event => onSetCardTypeId(event.currentTarget.value)}
                    size='sm'
                    value={cardTypeId ? String(cardTypeId) : ''}
                  />
                ) : (
                  <InPlaceSelect
                    allowUpdate
                    height='45px'
                    options={cardTypeOptions}
                    value={cardTypeId}
                    onChange={onSetCardTypeId}
                  />
                )}
              </OptionField>
              <AddButtonWrapper>
                {siteIsSeedlang ? addButton : (
                  <SeedlangExplainer isPodcastEpisode={isPodcastEpisode}>
                    {addButton}
                  </SeedlangExplainer>
                )}
              </AddButtonWrapper>
            </OptionsRow>
            {
              selectedWordsExcludedFromGenderTrainer && cardTypeId === Constants.REVIEW_CARD_TYPES.find(item => item.slug === 'gender')?.id &&
              <Alert
                textAlign='center'
                width='100%'
              >
                Some nouns cannot be added as gender cards
                (<i>{selectedWordsExcludedFromGenderTrainer.join(', ')}</i>).
              </Alert>
            }
            {
              selectedWordsExcludedFromPluralTrainer && cardTypeId === Constants.REVIEW_CARD_TYPES.find(item => item.slug === 'plural')?.id &&
              <Alert
                textAlign='center'
                width='100%'
              >
                Some nouns cannot be added as gender cards
                (<i>{selectedWordsExcludedFromPluralTrainer.join(', ')}</i>).
              </Alert>
            }
            {
              selectedWordsExcludedFromConjugationTrainer && cardTypeId === Constants.REVIEW_CARD_TYPES.find(item => item.slug === 'conjugation')?.id &&
              <Alert
                textAlign='center'
                width='100%'
              >
                Some verbs cannot be added as conjugation cards at the moment
                (<i>{selectedWordsExcludedFromConjugationTrainer.join(', ')}</i>).
              </Alert>
            }
          </ReviewOptionsWrapper>
      }
    </Wrapper>
  );
};

const AddToReviewsWrapper = observer(props => (
  <AddToReviews
    {...props}
    deckId={props.deckId || AppUI.defaultReviewDeckId}
    newReviewDeckName={VocabUI.newReviewDeckName}
    onCreateNewReviewDeck={() => VocabUI.onCreateNewReviewDeck(() => AppUI.loadReviewDecks())}
    onSetNewReviewDeckName={val => VocabUI.onSetNewReviewDeckName(val)}
    siteIsSeedlang={AppUI.siteIsSeedlang}
  />
));

export default ErrorBoundary(AddToReviewsWrapper);
